import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import Container from '@/components/ui/container'
import Icon from '@/components/ui/icon'

interface InfoCard {
  icon: string
  title: string
  line1: string
  line2: string
}

function getHref(item: InfoCard): string | undefined {
  if (item.icon === 'mail') return `mailto:${item.line1}`
  if (item.icon === 'phone') return `tel:${item.line1.replace(/[^\d+]/g, '')}`
  return undefined
}

export default function ContactInfo() {
  const { t } = useTranslation()
  const { ref, visible } = useScrollReveal()
  const items = t('contact.info.items', { returnObjects: true }) as InfoCard[]

  return (
    <section ref={ref} className="py-16 md:py-24 lg:py-32">
      <Container>
        <div
          className={`text-center mb-10 md:mb-16 lg:mb-20 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-primary font-body text-label-md uppercase tracking-[0.4em] mb-4 block">
            {t('contact.info.label')}
          </span>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter uppercase">
            {t('contact.info.heading')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, i) => {
            const href = getHref(item)
            const className = `group p-5 md:p-8 border border-outline-variant/20 hover:border-primary/40 transition-all duration-500 ease-out text-center block ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`
            const style = { transitionDelay: visible ? `${300 + i * 100}ms` : '0ms' }
            const content = (
              <>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-6 text-primary">
                  <Icon name={item.icon} className="text-3xl" />
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight text-on-surface mb-3 uppercase">
                  {item.title}
                </h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">{item.line1}</p>
                <p className="text-on-surface-variant text-sm leading-relaxed">{item.line2}</p>
              </>
            )

            return href ? (
              <a key={i} href={href} className={className} style={style}>
                {content}
              </a>
            ) : (
              <div key={i} className={className} style={style}>
                {content}
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
